import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Linking, Image, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useThemeColors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { isDriverRole } from '../types/roles';
import { ratingDisplay } from '../services/ratingUtils';
import FichaPersona from './FichaPersona';

/** Renderiza ⭐ + número si hay rating real, o un chip "Nuevo" si no.
 *
 *  "SIN DATO" Y "CERO CALIFICACIONES" NO SON LO MISMO. Si `count` no llega
 *  (undefined/null) nadie nos dijo cuántas notas tiene esa persona: puede ser
 *  nueva o llevar cien viajes. En ese caso no se pinta NADA — ni estrellas ni
 *  "Nuevo" —, porque "Nuevo" también es una afirmación. `0` sí es un dato:
 *  existe y todavía no la ha calificado nadie. Antes los dos casos acababan en
 *  "Nuevo", porque `ratingDisplay` trata como 0 todo lo que no sea number. */
function RatingChip({ rating, count, textColor }: { rating?: any; count?: any; textColor: string }) {
  if (count === undefined || count === null) return null;
  const r = ratingDisplay(rating, count);
  if (r.isNew) {
    return (
      <View style={{ backgroundColor: 'rgba(255,149,0,0.15)', paddingHorizontal: 6, paddingVertical: 1, borderRadius: 6 }}>
        <Text style={{ fontSize: 11, fontWeight: '800', color: '#FF9500' }}>Nuevo</Text>
      </View>
    );
  }
  return (
    <>
      <Ionicons name="star" size={12} color="#FFD700" />
      <Text style={{ fontSize: 12, fontWeight: 'bold', color: textColor }}>{r.label}</Text>
    </>
  );
}

interface ActiveRideCardProps {
  userName: string;
  rating: string;
  vehicleInfo?: string;
  eta: string;
  onCancel: () => void;
  /** Foto (KYC) de la contraparte. Si soy PASSENGER llega la del chofer;
   *  si soy DRIVER llega la del pasajero. La mía viene del store. */
  otherPartyPhoto?: string | null;
  /** Datos del vehículo del chofer para mostrar Uber-style. Solo se muestran
   *  al pasajero (el chofer ya sabe qué carro tiene). */
  vehicleBrand?: string | null;
  vehicleModel?: string | null;
  vehicleColor?: string | null;
  licensePlate?: string | null;
  /** Teléfono de la contraparte para el botón "Llamar". Sin esto caía al
   *  número hardcoded de test. */
  otherPartyPhone?: string | null;
  /** ID del viaje — IMPRESCINDIBLE para el chat. Sin él, /chat se abría sin
   *  saber a qué conversación pertenecía y los mensajes nunca se enviaban. */
  rideId?: string | null;
  /** Estado del ride — controla qué botón muestra al chofer:
   *  ACCEPTED   → "He llegado"        (chofer va rumbo al pasajero)
   *  ARRIVED    → "Iniciar viaje"     (chofer espera al pasajero abordar)
   *  IN_PROGRESS→ "Completar viaje"   (viaje en curso hacia destino)
   *  Al pasajero solo le mostramos texto informativo del estado.
   */
  rideStatus?: 'ACCEPTED' | 'ARRIVED' | 'IN_PROGRESS';
  onDriverArrived?: () => void;
  /**
   * Lo toca el chofer cuando lleva los minutos esperando y el pasajero no
   * aparece. Como con `onDriverArrived`, la tarjeta decide si enseña el botón
   * por la EXISTENCIA de esta función: al pasajero no se le pasa, así que no
   * le sale.
   */
  onCancelarPorEspera?: () => void;
  onStartRide?: () => void;
  onCompleteRide?: () => void;
  /** Cantidad de calificaciones reales de la contraparte. Si 0/undefined,
   *  se muestra "Nuevo" en vez de estrellas falsas. */
  ratingCount?: number | null;
  /** El pasajero confirmó identidad; si el chofer ya llegó (ARRIVED) esto
   *  arranca el viaje automáticamente (el padre llama a startRide). */
  onPassengerConfirm?: () => void;
  /** PIN de viaje (Uber-style) de 4 dígitos. Se muestra SOLO al pasajero
   *  (ACCEPTED/ARRIVED) para que se lo diga al chofer, quien lo ingresa para
   *  iniciar el viaje. */
  ridePin?: string | null;
  /** Momento (ms) en que el chofer marcó su llegada. Alimenta el contador de
   *  espera. Si no llega, el contador simplemente no se muestra. */
  arrivedAtMs?: number | null;
  /** ¿La CONTRAPARTE pasó de verdad la verificación de identidad (KYC)?
   *  NO se llama "biométrica" ni aquí ni en pantalla: la selfie la compara a
   *  mano una persona autorizada (ver terms.tsx y privacy.tsx).
   *  Si soy PASAJERO es el chofer; si soy CHOFER es el pasajero.
   *  SIN DATO NO SE PINTA ESCUDO: `undefined`/`null` significa "no lo sé".
   *  Antes esto era el literal `true` dentro del componente, así que la
   *  tarjeta afirmaba que la otra persona estaba verificada aunque nadie lo
   *  hubiera comprobado nunca. Mi propia verificación NO viene por aquí: sale
   *  del store, que es el único sitio donde el dato es mío y es real. */
  otherPartyVerified?: boolean | null;
  /** Mismo dato con el nombre que usa `RideOfferCard`. Existe solo para que
   *  la pantalla que las pinta a las dos pueda usar un nombre u otro sin que
   *  el valor real se pierda por el camino. Si llegan los dos, manda
   *  `otherPartyVerified`. */
  isFaceIdVerified?: boolean | null;
  /**
   * EL RANGO DEL CHOFER ('BRONCE' | 'PLATA' | 'ORO' | 'PLATINO' | 'DIAMANTE').
   *
   * Lo calcula el SERVIDOR (functions/src/niveles.ts) con los viajes cumplidos
   * y la nota; el chofer no puede escribirlo. Viaja estampado en la oferta y
   * tambien lo devuelve `getDriverPublicInfo`, asi que sobrevive a cerrar y
   * reabrir la app. Sin dato no se pinta nada: un rango inventado en el
   * cliente vale menos que ninguno.
   */
  driverLevel?: string | null;
  /** Viajes cumplidos del chofer, para que el rango no sea una palabra suelta. */
  driverCompletedTrips?: number | null;
  /**
   * LA FOTO DEL CARRO, la que estampo el servidor (`vehiculo_publico/`,
   * functions/src/fotoVehiculo.ts) — nunca una que haya subido el chofer sin
   * pasar por ahi. Es donde se lee la placa: el pasajero compara ese numero con
   * el carro que tiene delante ANTES de montarse.
   *
   * ⚠️ A DIFERENCIA DEL RANGO, ESTA NO SOBREVIVE A REABRIR LA APP. Viaja en la
   * OFERTA, no en el documento del viaje, y la lista blanca de
   * `getDriverPublicInfo` (functions/src/drivers.ts) no la devuelve. Si alguien
   * la quiere de vuelta tras un reinicio, el sitio es esa lista blanca — NO
   * leerla de `rides/{id}`, donde no la escribe nadie (medido el 17-oct-2026).
   */
  vehiclePhotoUrl?: string | null;
  /**
   * QUIEN ES ESTA PERSONA **EN ESTE VIAJE** — no en que modo esta la app.
   *
   * ⚠️ ESTO ARREGLA "LAS DOS PANTALLAS ESTAN CRUZADAS".
   *
   * Antes el papel salia de `useAppStore(s => s.userRole)`, que es el MODO que
   * el usuario eligio en la portada y que queda guardado en disco. Una pasajera
   * cuyo telefono hubiera estado alguna vez en Modo Conductor veia su propia
   * tarjeta al reves: ella como "Tu (Conductor)" y su chofer como pasajero.
   *
   * Y no eran solo las etiquetas. De ese mismo dato cuelgan la foto de cada
   * fila, los dos sellos de verificado y QUE PLACA se muestra: con el papel
   * volteado, a la pasajera se le pintaba SU vehiculo (vacio) en el sitio donde
   * tenia que ir el del chofer. Eso es justo lo que el fundador reporto como
   * "datos erroneos en los perfiles": es el mismo fallo, no dos.
   *
   * Quien pinta la tarjeta SABE en que rama esta — map.tsx tiene una rama para
   * el pasajero (usa `selectedDriver`) y otra para el chofer (usa
   * `driverCurrentRide`)—, asi que lo dice explicitamente. Si no llega, se cae
   * al modo de la app como antes: un respaldo, no la fuente.
   */
  soyElChoferDeEsteViaje?: boolean;
  /**
   * La cancelación está esperando al servidor (H05/H07, auditoría 16-sep-2026).
   * Desde que "Cancelar Viaje" solo se da por hecho cuando el servidor lo
   * confirma, entre el toque y la respuesta pasa un rato: el botón lo dice y
   * no acepta un segundo toque. Sin la prop, el botón se porta como siempre.
   */
  cancelando?: boolean;
  /**
   * El aviso de llegada está esperando al servidor (H42, auditoría 16-sep-2026).
   * Las reglas aceptan ARRIVED→ARRIVED, así que un segundo toque en ese rato
   * reescribía `arrivedAt` y corría el contador de espera. Sin la prop, el botón
   * se porta como siempre.
   */
  marcandoLlegada?: boolean;
}

/**
 * Cuerpo real de la tarjeta. No se exporta: fuera se usa `ActiveRideCard`, que
 * es este mismo componente memoizado más el envoltorio que estabiliza los
 * manejadores (ver abajo el porqué).
 */
const TarjetaViajeActivoBase = ({
  userName,
  rating,
  vehicleInfo,
  eta,
  onCancel,
  otherPartyPhoto,
  vehicleBrand,
  vehicleModel,
  vehicleColor,
  licensePlate,
  otherPartyPhone,
  rideId,
  rideStatus = 'ACCEPTED',
  onDriverArrived,
  onCancelarPorEspera,
  onStartRide,
  onCompleteRide,
  // SIN valor por defecto a propósito. Con `= 0`, la tarjeta convertía en
  // "Nuevo" a cualquiera cuyo número de calificaciones no le hubieran pasado:
  // borraba la diferencia entre "no lo sé" y "nadie lo ha calificado" antes
  // siquiera de consultar el ayudante. Ver RatingChip.
  ratingCount,
  onPassengerConfirm,
  ridePin,
  arrivedAtMs = null,
  // Sin valor por defecto a propósito: "no lo sé" tiene que poder distinguirse
  // de "sí". Ver el porqué en la declaración de la prop.
  otherPartyVerified,
  isFaceIdVerified,
  driverLevel,
  driverCompletedTrips,
  vehiclePhotoUrl,
  soyElChoferDeEsteViaje,
  cancelando = false,
  marcandoLlegada = false,
}: ActiveRideCardProps) => {
  const colors = useThemeColors();
  // ── H71 (auditoría 16-sep-2026): EL BLOQUE DE IDENTIDAD EN MODO OSCURO ──────
  // Las filas de "Verificación de identidad" tenían fondos claros FIJOS
  // (#FFFFFF, #F9FAFC, #F5F7FA) y el texto encima usa `colors.text`, que en
  // oscuro es blanco: nombre del chofer, del pasajero, carro y nota quedaban
  // blanco sobre blanco, y el título (#0A2540) azul marino sobre la tarjeta
  // oscura. Justo la tarjeta de seguridad con la que se vende la app.
  //
  // En oscuro se usan los tokens que ya existen (`card`, `backgroundElement`,
  // `text`); en claro se dejan los valores de siempre, píxel por píxel: el
  // arreglo no le cambia la cara a nadie que no haya encendido el modo oscuro.
  // Se mira `theme` igual que `useThemeColors` (y `DesktopSidebarLayout`).
  // Van como objetos enteros o `null` (nunca `{ backgroundColor: undefined }`):
  // un `undefined` en el arreglo de estilos PISA el valor de la hoja y dejaría la
  // fila transparente también en claro.
  const esOscuro = useAppStore((state) => state.theme) === 'dark';
  const oscuroFilaPasajero = esOscuro ? { backgroundColor: colors.card } : null;
  const fondoFilaChofer = { backgroundColor: esOscuro ? colors.backgroundElement : '#F9FAFC' };
  const oscuroNota = esOscuro ? { backgroundColor: colors.backgroundElement } : null;
  const oscuroTituloIdentidad = esOscuro ? { color: colors.text } : null;
  const router = useRouter();
  const role = useAppStore((state) => state.userRole);
  const userPhoto = useAppStore((state) => state.userPhoto);
  const myVehicleBrand = useAppStore((state) => state.vehicleBrand);
  const myVehicleModel = useAppStore((state) => state.vehicleModel);
  const myVehicleColor = useAppStore((state) => state.vehicleColor);
  const myLicensePlate = useAppStore((state) => state.licensePlate);
  // Mi propia verificación (la de la otra persona llega por props): el store la
  // rehidrata desde Firestore al abrir la app, así que es el dato real y no una
  // suposición del componente.
  const myVerified = useAppStore((state) => state.isVerified);
  const myDriverVerified = useAppStore((state) => state.isDriverVerified);

  /**
   * ¿SOY YO QUIEN CONDUCE ESTE VIAJE?
   *
   * Antes esto era el literal `role === 'DRIVER'` repetido por toda la
   * tarjeta, y ese literal deja fuera a dos roles que SÍ pueden llegar aquí
   * conduciendo. Comprobado en `firestore.rules` (bloque `/rides`): leer los
   * viajes PENDING lo permite `hasAnyRole(['DRIVER','DELIVERY_DRIVER',
   * 'SCHOOL_DRIVER'])`, y para CREAR la oferta y para reclamar el viaje la
   * regla no mira el rol en absoluto — solo `isVerifiedDriver()`, que lee
   * `isDriverVerified`. O sea que un repartidor o un chofer escolar
   * verificado puede aceptar un viaje de pasajero.
   *
   * Y al aceptarlo se quedaba sin nada con que avanzarlo: con el pasajero
   * dentro del carro y sin «He llegado», sin «Iniciar viaje» y sin «Completar
   * viaje». Peor todavía, la tarjeta lo pintaba como si fuera el pasajero —
   * su foto en la fila de arriba, «El conductor está en camino» dicho al
   * conductor, y su sello leído del campo de KYC en vez del de chofer.
   *
   * `isDriverRole` (types/roles.ts) es la lista única de roles que conducen,
   * la misma que usan las reglas. Para `DRIVER` y para `PASSENGER` esto no
   * cambia absolutamente nada: solo deja de excluir a los otros dos.
   */
  // EL PAPEL DE ESTE VIAJE MANDA SOBRE EL MODO DE LA APP.
  // `soyElChoferDeEsteViaje` lo pasa quien pinta la tarjeta, que sabe cual de
  // las dos ramas esta renderizando. `isDriverRole(role)` queda de respaldo por
  // si alguna llamada futura no lo pasa — pero es el respaldo, no la fuente.
  const soyChofer =
    typeof soyElChoferDeEsteViaje === 'boolean' ? soyElChoferDeEsteViaje : isDriverRole(role);
  // Lo mismo para "soy el pasajero": era `role === 'PASSENGER'` en seis sitios,
  // y con el modo volteado los seis mentian a la vez.
  const soyPasajero = !soyChofer;

  const [isVisualConfirmed, setIsVisualConfirmed] = useState(false);
  /**
   * Qué ficha está abierta al tocar una de las dos fotos. `null` = ninguna.
   * Antes los dos círculos eran `<View>` pelados y tocarlos no hacía nada.
   */
  const [ficha, setFicha] = useState<'DRIVER' | 'PASSENGER' | null>(null);

  // Resolvemos la foto real de cada parte SIN caer al Unsplash de fondo.
  // Antes teníamos URLs `photo-XXX@unsplash.com` como fallback → todos los
  // rides se veían con las mismas dos caras genéricas. Ahora si no hay foto
  // real, mostramos un placeholder de ícono (avatar neutro), NUNCA una cara
  // ajena. Esto también respeta el KYC biométrico como fuente de verdad.
  const passengerPhoto: string | null = soyPasajero
    ? (userPhoto || null)
    : (otherPartyPhoto ?? null);
  const driverPhoto: string | null = soyChofer
    ? (userPhoto || null)
    : (otherPartyPhoto ?? null);

  // ── QUIÉN ESTÁ VERIFICADO DE VERDAD ──────────────────────────────────────
  // Mismo criterio que las fotos: lo mío sale del store (es mi dato y es real)
  // y lo de la otra persona tiene que LLEGAR de fuera. Antes aquí había un
  // `true` fijo, así que la tarjeta ponía escudo verde y "Verificado" a los
  // dos lados pasara lo que pasara. Un sello que siempre dice que sí no
  // informa de nada. Sin dato no se pinta escudo: se dice el papel a secas
  // ("Pasajero"), nunca "Pasajero Verificado".
  const contraparteVerificada = (otherPartyVerified ?? isFaceIdVerified) === true;
  const yoVerificado = soyChofer ? myDriverVerified === true : myVerified === true;
  const pasajeroVerificado = soyPasajero ? yoVerificado : contraparteVerificada;
  const choferVerificado = soyChofer ? yoVerificado : contraparteVerificada;

  /** ¿La ficha abierta es la MÍA? (misma cuenta que la de la foto tocada). */
  const esMiFicha =
    (ficha === 'DRIVER' && soyChofer) ||
    (ficha === 'PASSENGER' && soyPasajero);

  // Info del vehículo — al pasajero le mostramos la de la contraparte (que
  // llega en props desde el ride offer). El chofer ve la suya del store.
  const shownBrand = soyChofer ? myVehicleBrand : vehicleBrand;
  const shownModel = soyChofer ? myVehicleModel : vehicleModel;
  const shownColor = soyChofer ? myVehicleColor : vehicleColor;
  const shownPlate = soyChofer ? myLicensePlate : licensePlate;
  const vehicleLine = [shownColor, shownBrand, shownModel].filter(Boolean).join(' ') || vehicleInfo || null;

  /**
   * ¿Este viaje es el de demostración? El id lo dice.
   *
   * Hace falta aquí dentro porque los dos botones de abajo apuntan a una
   * persona que no existe, y los dos fallaban con un mensaje que le echaba la
   * culpa a la red del usuario:
   *   · «Mensaje» abría /chat con el id falso. El listener lee
   *     /rides/{id}/messages, cuya regla hace `get(/rides/{rideId})` sobre un
   *     viaje inexistente → denegado → «No pudimos cargar los mensajes. Revisa
   *     tu conexión y vuelve a entrar al chat».
   *   · «Llamar» no tenía teléfono (la oferta simulada no trae `driverPhone`),
   *     así que decía «Sin número de contacto… Usa el chat para comunicarte»,
   *     empujando justo al botón roto.
   * Dos acusaciones falsas de conexión seguidas es lo que hace que alguien
   * concluya que la app está rota — y en una revisión, que la rechace.
   */
  const esDemostracion = String(rideId ?? '').startsWith('qa_mock_ride_');

  const avisoDeDemostracion = (que: string) =>
    Alert.alert(
      'Esto es una demostración',
      `${que} En un viaje de verdad, aquí hablas con tu chofer.`
    );

  const handleCall = () => {
    if (esDemostracion) {
      avisoDeDemostracion('Carlos R. no existe, así que no hay a quién llamar.');
      return;
    }
    // L6 FIX — Antes hardcoded al número de test +1-829-555-1234. Ahora
    // usamos el teléfono real de la contraparte. Si no llega (ride viejo,
    // usuario privado, etc.) mostramos alert en vez de marcar al número
    // falso.
    const raw = String(otherPartyPhone ?? '').trim();
    if (!raw) {
      Alert.alert(
        'Sin número de contacto',
        'No pudimos leer el teléfono de la otra persona. Usa el chat para comunicarte.'
      );
      return;
    }
    // Quitar espacios y guiones para el URI tel:
    const clean = raw.replace(/[\s\-()]/g, '');
    Linking.openURL(`tel:${clean}`).catch(() => {
      Alert.alert('No pudimos abrir la llamada', 'Marca directamente el número: ' + raw);
    });
  };

  const handleChat = () => {
    if (esDemostracion) {
      avisoDeDemostracion('Carlos R. no existe, así que no hay con quién chatear.');
      return;
    }
    // Antes: router.push('/chat') SIN datos → la pantalla no sabía a qué viaje
    // pertenecía y los mensajes se quedaban en el teléfono. Ahora le pasamos el
    // rideId (la conversación real) y los datos de la contraparte.
    router.push({
      pathname: '/chat',
      params: {
        rideId: rideId ?? '',
        partnerName: userName ?? '',
        partnerPhone: otherPartyPhone ?? '',
      },
    } as any);
  };

  const handleConfirmPhoto = () => {
    setIsVisualConfirmed(true);
    // FIX AUTO-START (#5) — Si soy el PASAJERO y el chofer ya llegó (ARRIVED),
    // confirmar la identidad ARRANCA el viaje automáticamente (onPassengerConfirm
    // llama a startRide en el padre). Antes este botón era solo visual y el
    // viaje solo lo iniciaba el chofer a mano.
    const willAutoStart = soyPasajero && rideStatus === 'ARRIVED' && !!onPassengerConfirm;
    if (willAutoStart) onPassengerConfirm!();
    Alert.alert(
      "Identidad Confirmada",
      willAutoStart
        ? "¡Excelente! Confirmaste la identidad. Tu viaje inicia ahora automáticamente."
        : "¡Excelente! Has confirmado visualmente la identidad de la otra persona.",
      [{ text: "Entendido" }]
    );
  };

  return (
    <View style={[styles.card, { backgroundColor: colors.card, shadowColor: colors.text }]}>
      {/* Estado del viaje — texto contextual según protocolo Uber-style.
          El label cambia con `rideStatus` para que ambas partes sepan
          exactamente en qué momento están del viaje. */}
      {(() => {
        const statusInfo = (() => {
          if (rideStatus === 'IN_PROGRESS') {
            return { text: 'Viaje en Curso • Seguro', color: '#4CAF50', pulse: false };
          }
          if (rideStatus === 'ARRIVED') {
            return soyChofer
              ? { text: 'Esperando al pasajero', color: '#FF9500', pulse: true }
              : { text: 'Tu chofer ha llegado', color: '#FF9500', pulse: true };
          }
          // ACCEPTED (default)
          return soyChofer
            ? { text: 'En camino al pasajero', color: '#00C853', pulse: true }
            : { text: 'El chofer está en camino', color: '#00C853', pulse: true };
        })();
        return (
          <View style={styles.statusRow}>
            <View style={[styles.statusBadge, { backgroundColor: `${statusInfo.color}22` }]}>
              {statusInfo.pulse ? (
                <View style={[styles.pulseDot, { backgroundColor: statusInfo.color }]} />
              ) : (
                <Ionicons name="checkmark-circle" size={14} color={statusInfo.color} style={{ marginRight: 6 }} />
              )}
              <Text style={[styles.statusText, { color: statusInfo.color }]}>{statusInfo.text}</Text>
            </View>
            <Text style={[styles.etaText, { color: colors.text }]}>
              {rideStatus === 'IN_PROGRESS' ? 'En viaje' : rideStatus === 'ARRIVED' ? '¡Ya llegó!' : eta}
            </Text>
          </View>
        );
      })()}

      {/* PIN DE VIAJE (Uber-style) — SOLO al pasajero, antes de arrancar.
          Se lo dice al chofer para que este inicie el viaje. */}
      {soyPasajero && !!ridePin && (rideStatus === 'ACCEPTED' || rideStatus === 'ARRIVED') && (
        <View style={styles.pinBox}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
            <Ionicons name="keypad" size={16} color="#00796B" style={{ marginRight: 6 }} />
            <Text style={styles.pinLabel}>Dale este PIN a tu chofer para iniciar</Text>
          </View>
          <View style={styles.pinDigits}>
            {String(ridePin).split('').map((d, i) => (
              <View key={i} style={styles.pinDigitBox}>
                <Text style={styles.pinDigitText}>{d}</Text>
              </View>
            ))}
          </View>
        </View>
      )}

      <View style={[styles.divider, { backgroundColor: colors.border }]} />

      {/* FOTOS CRUZADAS (Split Layout) */}
      {/* ── ESTE TÍTULO NO PUEDE VOLVER A DECIR "BIOMÉTRICO" ────────────────
          Decía "Perfil Biométrico de Seguridad" y los Términos de la app dicen
          justo lo contrario, en negrita: la selfie de verificación "la compara
          a mano una persona autorizada de Go Safely" y "no usamos
          reconocimiento facial, prueba de vida ni ningún sistema automático"
          (src/app/terms.tsx, Registro y Verificación; la Política de
          Privacidad lo repite en src/app/privacy.tsx). Mientras la comparación
          la haga una persona, aquí solo cabe hablar de verificación de
          identidad. Cada fila de abajo dice por su cuenta si esa parte está
          verificada o no, así que el título no afirma nada de nadie. */}
      <Text style={[styles.crossPhotosHeader, oscuroTituloIdentidad]}>Verificación de identidad</Text>
      
      {/* Ficha de la persona cuya foto se tocó. Los datos del vehículo solo
          tienen sentido en la del chofer, por eso van condicionados. */}
      <FichaPersona
        visible={ficha !== null}
        onCerrar={() => setFicha(null)}
        datos={{
          tipo: ficha === 'DRIVER' ? 'DRIVER' : 'PASSENGER',
          // `userName` es SIEMPRE el nombre de la CONTRAPARTE (así lo pasa
          // map.tsx). El propio usuario se rotula "Tú" para que no parezca que
          // se está mirando a sí mismo.
          nombre: esMiFicha ? 'Tú' : userName,
          foto: ficha === 'DRIVER' ? driverPhoto : passengerPhoto,
          // `rating`/`ratingCount` son SIEMPRE de la contraparte. Si la ficha
          // abierta es la mía, no tengo mi propia nota aquí: no se pinta la de
          // la otra persona como si fuera mía. FichaPersona dirá "Sin
          // calificaciones aún". Es el mismo criterio del chip de abajo.
          calificacion: esMiFicha ? null : rating,
          numeroCalificaciones: esMiFicha ? null : ratingCount,
          // Antes: `true` fijo. Ahora cada quien lleva SU verificación real.
          verificado: ficha === 'DRIVER' ? choferVerificado : pasajeroVerificado,
          marca: ficha === 'DRIVER' ? shownBrand : null,
          modelo: ficha === 'DRIVER' ? shownModel : null,
          color: ficha === 'DRIVER' ? shownColor : null,
          placa: ficha === 'DRIVER' ? shownPlate : null,
          // ── EL RANGO Y EL CARRO, QUE SE PERDIAN EN ESTA PANTALLA ─────────
          // Los dos datos YA venian en la oferta y `FichaPersona` YA sabe
          // pintarlos; este objeto simplemente no los incluia, asi que el
          // pasajero los veia al elegir chofer y los perdia en la pantalla
          // siguiente. La app se contradecia sola en dos pantallas seguidas.
          //
          // `!soyChofer`: solo cuando la ficha abierta es la del chofer y YO
          // soy el pasajero. Un chofer mirando su propia ficha no necesita que
          // le recuerden su rango, y mirando la del PASAJERO no hay ni rango
          // ni carro que ensenar — los rangos son del chofer a proposito
          // (BarraDeNivel: `if (!esChofer) return filaNota`).
          nivel: ficha === 'DRIVER' && !soyChofer ? (driverLevel ?? null) : null,
          viajesCompletados:
            ficha === 'DRIVER' && !soyChofer ? (driverCompletedTrips ?? null) : null,
          fotoVehiculo: ficha === 'DRIVER' && !soyChofer ? (vehiclePhotoUrl ?? null) : null,
        }}
      />

      <View style={[styles.crossPhotosContainer, { borderColor: colors.border }]}>
        {/* PARTE SUPERIOR: Pasajero */}
        <View style={[styles.crossPhotoRow, oscuroFilaPasajero]}>
          <TouchableOpacity
            style={styles.crossAvatarWrapper}
            onPress={() => setFicha('PASSENGER')}
            accessibilityRole="button"
            accessibilityLabel="Ver los datos del pasajero"
          >
            {passengerPhoto ? (
              <Image source={{ uri: passengerPhoto }} style={styles.crossAvatar} />
            ) : (
              <View style={[styles.crossAvatar, styles.crossAvatarPlaceholder]}>
                <Ionicons name="person" size={22} color="#829AB1" />
              </View>
            )}
            {/* El escudo solo si el pasajero está verificado de verdad. */}
            {pasajeroVerificado && (
              <View style={styles.crossShieldBadge}>
                <Ionicons name="shield-checkmark" size={10} color="#FFF" />
              </View>
            )}
          </TouchableOpacity>
          <View style={styles.crossInfo}>
            <Text style={[styles.crossRoleLabel, { color: colors.textSecondary }]}>
              {pasajeroVerificado ? 'Pasajero Verificado' : 'Pasajero'}
            </Text>
            {/* H74 — un nombre dominicano completo ocupa tres líneas y estiraba
                la tarjeta hasta sacar "Cancelar Viaje" de la hoja. Una línea con
                puntos suspensivos; el nombre entero está en la ficha al tocar la foto. */}
            <Text style={[styles.crossName, { color: colors.text }]} numberOfLines={1} ellipsizeMode="tail">
              {soyPasajero ? 'Tú (Pasajero)' : (userName || 'Pasajero')}
            </Text>
          </View>
          <View style={[styles.crossStatus, oscuroNota]}>
            {/* `rating`/`ratingCount` son SIEMPRE de la contraparte. Si el
                pasajero soy yo, aquí no tengo mi propia nota: eso es SIN DATO
                (`undefined`), no "cero calificaciones". Antes se pasaba `0` y
                la tarjeta me rotulaba "Nuevo" a mí mismo aunque llevara
                cincuenta viajes calificados. */}
            <RatingChip
              rating={soyPasajero ? undefined : rating}
              count={soyPasajero ? undefined : ratingCount}
              textColor={colors.text}
            />
          </View>
        </View>

        {/* Separador de las dos fotos */}
        <View style={[styles.crossDivider, { backgroundColor: colors.border }]} />

        {/* PARTE INFERIOR: Conductor y su vehículo (Uber-style) */}
        <View style={[styles.crossPhotoRow, fondoFilaChofer]}>
          <TouchableOpacity
            style={styles.crossAvatarWrapper}
            onPress={() => setFicha('DRIVER')}
            accessibilityRole="button"
            accessibilityLabel="Ver los datos del chofer y su vehiculo"
          >
            {driverPhoto ? (
              <Image source={{ uri: driverPhoto }} style={styles.crossAvatar} />
            ) : (
              <View style={[styles.crossAvatar, styles.crossAvatarPlaceholder]}>
                <Ionicons name="person" size={22} color="#829AB1" />
              </View>
            )}
            {/* El escudo solo si el chofer está verificado de verdad. */}
            {choferVerificado && (
              <View style={styles.crossShieldBadge}>
                <Ionicons name="shield-checkmark" size={10} color="#FFF" />
              </View>
            )}
          </TouchableOpacity>
          <View style={styles.crossInfo}>
            <Text style={[styles.crossRoleLabel, { color: colors.textSecondary }]}>
              {choferVerificado ? 'Chofer Verificado' : 'Chofer'}
            </Text>
            {/* H74 — nombre y carro en una línea cada uno (ver la fila de arriba). */}
            <Text style={[styles.crossName, { color: colors.text }]} numberOfLines={1} ellipsizeMode="tail">
              {soyChofer ? 'Tú (Chofer)' : (userName || 'Chofer')}
            </Text>
            {vehicleLine && (
              <Text style={[styles.crossVehicle, { color: colors.text, fontWeight: '600' }]} numberOfLines={1} ellipsizeMode="tail">
                {vehicleLine}
              </Text>
            )}
            {shownPlate && (
              <View style={styles.plateBadge}>
                <Text style={styles.plateText}>{shownPlate}</Text>
              </View>
            )}
          </View>
          <View style={[styles.crossStatus, oscuroNota]}>
            {/* Mismo criterio que arriba: si el chofer soy yo, mi nota no
                viaja en estas props → SIN DATO, no cero. */}
            <RatingChip
              rating={soyChofer ? undefined : rating}
              count={soyChofer ? undefined : ratingCount}
              textColor={colors.text}
            />
          </View>
        </View>
      </View>

      {/* ── CONTADOR DE ESPERA ─────────────────────────────────────────────
          Arranca cuando el chofer marca su llegada y el pasajero todavía no se
          ha subido. Lo pidió el fundador: "no tenemos un contador que comienza
          cuando el conductor llega a recoger al pasajero y este no sube de una
          vez al carro".

          Lo ven LOS DOS, y a propósito: al chofer le dice cuánto lleva parado,
          y al pasajero le recuerda que hay alguien esperándolo. Un contador que
          solo ve una parte no cambia el comportamiento de la otra.

          No inventa ningún cobro: los primeros minutos son de cortesía y, al
          pasarlos, se avisa. Poner un precio por minuto es una decisión del
          dueño, no algo que deba salir del código sin que él lo decida.

          El dato viene de `arrivedAt`, que ya escribía el servidor: no hizo
          falta guardar nada nuevo. */}
      {rideStatus === 'ARRIVED' && arrivedAtMs ? (
        <ContadorDeEspera
          desdeMs={arrivedAtMs}
          soyChofer={soyChofer}
          colors={colors}
          onCancelarPorEspera={onCancelarPorEspera}
        />
      ) : null}

      {/* BOTÓN DE PROGRESO DE ESTADO (SOLO CHOFER) — Uber-style.
          Cada estado tiene UNA sola acción primaria: llegar → iniciar → completar.
          Al pasajero NO le mostramos estos botones (él es pasivo mientras el
          chofer avanza el flujo). */}
      {soyChofer && (
        <View style={{ marginBottom: 12 }}>
          {rideStatus === 'ACCEPTED' && onDriverArrived && (
            <TouchableOpacity
              style={[styles.progressBtn, { backgroundColor: '#FF9500' }]}
              onPress={onDriverArrived}
              activeOpacity={0.85}
              // H42 — mientras se avisa la llegada no admite otro toque.
              disabled={marcandoLlegada}
              accessibilityState={{ disabled: marcandoLlegada, busy: marcandoLlegada }}
            >
              <Ionicons name="flag" size={20} color="#FFF" style={{ marginRight: 8 }} />
              <Text style={styles.progressBtnText}>
                {marcandoLlegada ? 'Avisando tu llegada…' : 'He llegado al punto de recogida'}
              </Text>
            </TouchableOpacity>
          )}
          {rideStatus === 'ARRIVED' && onStartRide && (
            <TouchableOpacity
              style={[styles.progressBtn, { backgroundColor: '#00C853' }]}
              onPress={onStartRide}
              activeOpacity={0.85}
            >
              <Ionicons name="play" size={20} color="#FFF" style={{ marginRight: 8 }} />
              <Text style={styles.progressBtnText}>Iniciar viaje (pasajero abordó)</Text>
            </TouchableOpacity>
          )}
          {rideStatus === 'IN_PROGRESS' && onCompleteRide && (
            <TouchableOpacity
              style={[styles.progressBtn, { backgroundColor: '#2196F3' }]}
              onPress={onCompleteRide}
              activeOpacity={0.85}
            >
              <Ionicons name="checkmark-done" size={20} color="#FFF" style={{ marginRight: 8 }} />
              <Text style={styles.progressBtnText}>Completar viaje</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* CONFIRMACIÓN VISUAL BUTTON */}
      <View style={{ marginBottom: 16 }}>
        {isVisualConfirmed ? (
          <View style={styles.confirmedVisualBadge}>
            <Ionicons name="checkmark-circle" size={20} color="#4CAF50" style={{ marginRight: 8 }} />
            <Text style={styles.confirmedVisualText}>Confirmaste la identidad</Text>
          </View>
        ) : (
          <TouchableOpacity 
            style={[styles.confirmVisualBtn, { backgroundColor: colors.accent }]} 
            onPress={handleConfirmPhoto}
            activeOpacity={0.85}
          >
            <Ionicons name="camera" size={18} color="#FFF" style={{ marginRight: 8 }} />
            <Text style={styles.confirmVisualBtnText}>Confirmo que es la persona de la foto</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Communication Buttons */}
      <View style={styles.communicationRow}>
        <TouchableOpacity style={[styles.commButton, { backgroundColor: 'rgba(76, 175, 80, 0.1)' }]} onPress={handleCall}>
          <Ionicons name="call" size={20} color="#4CAF50" />
          <Text style={[styles.commText, { color: '#4CAF50', fontSize: 14 }]}>Llamar</Text>
        </TouchableOpacity>
        
        <TouchableOpacity style={[styles.commButton, { backgroundColor: 'rgba(33, 150, 243, 0.1)' }]} onPress={handleChat}>
          <Ionicons name="chatbubbles" size={20} color="#2196F3" />
          <Text style={[styles.commText, { color: '#2196F3', fontSize: 14 }]}>Mensaje</Text>
        </TouchableOpacity>
      </View>

      {/* Cancel Button */}
      <TouchableOpacity
        style={styles.cancelWrapper}
        onPress={onCancel}
        disabled={cancelando}
        accessibilityState={{ disabled: cancelando, busy: cancelando }}
      >
        <Text style={[styles.cancelText, { color: cancelando ? colors.textSecondary : colors.error }]}>
          {cancelando ? 'Cancelando…' : 'Cancelar Viaje'}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

/**
 * La tarjeta memoizada. Comparación superficial por defecto, a propósito: todas
 * sus props de datos son primitivas (textos, números, null), así que la
 * comparación por defecto es exacta y NO puede perderse una actualización — que
 * es el riesgo real de escribir un comparador a mano.
 */
const TarjetaViajeActivo = React.memo(TarjetaViajeActivoBase);

/**
 * Envoltorio público.
 *
 * POR QUÉ EXISTE: el padre (`map.tsx`) pasa `onCancel`, `onDriverArrived`, etc.
 * como funciones flecha escritas en línea, o sea funciones NUEVAS en cada
 * render suyo. Con eso, un `React.memo` pelado sobre la tarjeta no ahorraría
 * nada: la comparación siempre daría "distinto" y las 825 líneas se volverían a
 * renderizar en cada tick de GPS. Ignorar esas funciones en un comparador
 * tampoco vale: se quedarían congeladas apuntando a un `currentRideId` viejo y
 * "Cancelar viaje" cancelaría el viaje equivocado.
 *
 * La salida es esta: este envoltorio SÍ se re-renderiza siempre (cuesta nada,
 * solo devuelve un elemento) y guarda las props frescas en una ref; hacia
 * dentro entrega manejadores de identidad fija que llaman a la versión más
 * reciente. Así el memo funciona y ningún manejador queda caduco.
 */
export const ActiveRideCard = (props: ActiveRideCardProps) => {
  const refProps = React.useRef(props);
  refProps.current = props;

  const estables = React.useMemo(
    () => ({
      onCancel: () => refProps.current.onCancel(),
      onDriverArrived: () => refProps.current.onDriverArrived?.(),
      onStartRide: () => refProps.current.onStartRide?.(),
      onCompleteRide: () => refProps.current.onCompleteRide?.(),
      onPassengerConfirm: () => refProps.current.onPassengerConfirm?.(),
    }),
    []
  );

  return (
    <TarjetaViajeActivo
      {...props}
      onCancel={estables.onCancel}
      /* Se conserva el `undefined` cuando el padre no manda el manejador: la
         tarjeta decide qué botón enseñar por la EXISTENCIA de la función
         (`rideStatus === 'ACCEPTED' && onDriverArrived`). Si aquí pusiéramos
         siempre una función, al pasajero le aparecerían los botones del
         chofer. */
      onDriverArrived={props.onDriverArrived ? estables.onDriverArrived : undefined}
      onStartRide={props.onStartRide ? estables.onStartRide : undefined}
      onCompleteRide={props.onCompleteRide ? estables.onCompleteRide : undefined}
      onPassengerConfirm={props.onPassengerConfirm ? estables.onPassengerConfirm : undefined}
    />
  );
};
ActiveRideCard.displayName = 'ActiveRideCard';

/**
 * Cuenta el tiempo que el chofer lleva esperando en el punto de recogida.
 *
 * Se actualiza cada segundo y se apaga solo al desmontarse: un intervalo que
 * sobrevive a la pantalla es de los que van vaciando la batería del chofer, que
 * tiene la app abierta todo el día.
 */
const ESPERA_CORTESIA_MIN = 3;

/**
 * A los diez minutos el chofer puede irse sin que le cueste.
 *
 * Decisión del fundador (3-oct-2026): «cuando un chofer llega a una ubicación y
 * el pasajero pasa de los 10 minutos, siempre y cuando el pasajero no aparezca,
 * puede cancelar sin penalidad y sin problema, pero siempre indicando el
 * motivo».
 *
 * Hasta ahora el contador solo CONTABA. Pasada la cortesía ponía el número en
 * naranja y ahí se acababa: si el chofer se iba, cancelaba como cualquier otra
 * cancelación y se llevaba el 20% de multa y una de las cinco faltas que le
 * bloquean la cuenta. El contador le medía la espera y después se la cobraba.
 *
 * ⚠️ Estos 10 minutos son los del RELOJ DEL TELÉFONO, y solo sirven para
 * ENSEÑAR el botón. Quien decide si la cancelación se perdona es el servidor,
 * con el sello de llegada y su propio reloj (`laEsperaFueDeVerdad`,
 * functions/src/penalties.ts). Un teléfono con la hora adelantada enseña el
 * botón antes de tiempo y el servidor cobra igual.
 */
const ESPERA_SIN_FALTA_MIN = 10;

function ContadorDeEspera({
  desdeMs,
  soyChofer,
  colors,
  onCancelarPorEspera,
}: {
  desdeMs: number;
  soyChofer: boolean;
  colors: any;
  onCancelarPorEspera?: () => void;
}) {
  const [ahora, setAhora] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const segundos = Math.max(0, Math.floor((ahora - desdeMs) / 1000));
  const mm = String(Math.floor(segundos / 60)).padStart(2, '0');
  const ss = String(segundos % 60).padStart(2, '0');
  const pasoLaCortesia = segundos >= ESPERA_CORTESIA_MIN * 60;
  const puedeIrseSinFalta = soyChofer && !!onCancelarPorEspera && segundos >= ESPERA_SIN_FALTA_MIN * 60;

  return (
    <>
      <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: pasoLaCortesia ? 'rgba(255,149,0,0.12)' : 'rgba(0,200,83,0.10)',
        borderRadius: 12,
        paddingHorizontal: 14,
        paddingVertical: 10,
        marginBottom: 12,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, marginRight: 10 }}>
        <Ionicons
          name={pasoLaCortesia ? 'alert-circle' : 'time-outline'}
          size={18}
          color={pasoLaCortesia ? '#FF9500' : '#00C853'}
          style={{ marginRight: 8 }}
        />
        <Text style={{ color: colors.text, fontSize: 13, flexShrink: 1 }}>
          {soyChofer
            ? pasoLaCortesia
              ? 'Llevas esperando más de los minutos de cortesía.'
              : 'Esperando a que el pasajero suba.'
            : pasoLaCortesia
              ? 'Tu chofer lleva rato esperándote. Sal cuanto antes.'
              : 'Tu chofer ya llegó y te está esperando.'}
        </Text>
      </View>
      <Text
        style={{
          color: pasoLaCortesia ? '#FF9500' : '#00C853',
          fontSize: 18,
          fontWeight: '900',
          fontVariant: ['tabular-nums'],
        }}
      >
        {mm}:{ss}
      </Text>
      </View>

      {puedeIrseSinFalta && (
        <TouchableOpacity
          onPress={onCancelarPorEspera}
          activeOpacity={0.85}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255,59,48,0.10)',
            borderWidth: 1,
            borderColor: 'rgba(255,59,48,0.45)',
            borderRadius: 12,
            paddingVertical: 12,
            paddingHorizontal: 14,
            marginBottom: 12,
          }}
        >
          <Ionicons name="exit-outline" size={18} color="#FF3B30" style={{ marginRight: 8 }} />
          <Text style={{ color: '#FF3B30', fontWeight: '700', fontSize: 14 }}>
            Irme sin que me cuente ({ESPERA_SIN_FALTA_MIN} min cumplidos)
          </Text>
        </TouchableOpacity>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 24,
    padding: 20,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    elevation: 8,
    marginBottom: 20,
    width: '100%',
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.02)',
  },
  statusRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 200, 83, 0.1)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  pulseDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#00C853',
    marginRight: 8,
  },
  statusText: {
    color: '#00C853',
    fontWeight: 'bold',
    fontSize: 12,
  },
  etaText: {
    fontSize: 16,
    fontWeight: '900',
  },
  divider: {
    height: 1,
    width: '100%',
    marginBottom: 16,
    opacity: 0.6,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  userName: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  ratingRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  ratingText: {
    fontSize: 13,
    fontWeight: '600',
    marginLeft: 4,
  },
  vehicleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    marginBottom: 16,
  },
  vehicleText: {
    fontSize: 14,
    fontWeight: '500',
  },
  communicationRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 20,
  },
  commButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 16,
    gap: 8,
  },
  commText: {
    fontWeight: 'bold',
    fontSize: 16,
  },
  cancelWrapper: {
    alignItems: 'center',
    paddingVertical: 8,
  },
  cancelText: {
    fontWeight: 'bold',
    fontSize: 14,
  },
  crossPhotosHeader: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#0A2540',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 8,
  },
  crossPhotosContainer: {
    borderWidth: 1,
    borderRadius: 16,
    overflow: 'hidden',
    marginBottom: 16,
  },
  crossPhotoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: '#FFFFFF',
  },
  crossAvatarWrapper: {
    position: 'relative',
    marginRight: 12,
  },
  crossAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#E1E8ED',
  },
  crossAvatarPlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  plateBadge: {
    marginTop: 4,
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#0A2540',
    backgroundColor: '#FFF9C4',
  },
  plateText: {
    fontSize: 12,
    fontWeight: '900',
    color: '#0A2540',
    letterSpacing: 1.5,
  },
  pinBox: {
    marginTop: 10,
    marginBottom: 4,
    padding: 12,
    borderRadius: 14,
    backgroundColor: 'rgba(0, 200, 83, 0.08)',
    borderWidth: 1,
    borderColor: 'rgba(0, 150, 136, 0.35)',
    alignItems: 'center',
  },
  pinLabel: {
    fontSize: 12.5,
    fontWeight: '700',
    color: '#00796B',
  },
  pinDigits: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  pinDigitBox: {
    width: 42,
    height: 52,
    marginHorizontal: 5,
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
    borderWidth: 1.5,
    borderColor: '#00796B',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pinDigitText: {
    fontSize: 26,
    fontWeight: '900',
    color: '#0A2540',
    letterSpacing: 1,
  },
  crossShieldBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#00C853',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: '#FFF',
  },
  crossInfo: {
    flex: 1,
    justifyContent: 'center',
  },
  crossRoleLabel: {
    fontSize: 10,
    fontWeight: '700',
    marginBottom: 2,
  },
  crossName: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  crossVehicle: {
    fontSize: 11,
    marginTop: 2,
  },
  crossStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#F5F7FA',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  crossRating: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  crossDivider: {
    height: 1,
    width: '100%',
  },
  progressBtn: {
    height: 54,
    borderRadius: 14,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 5,
  },
  progressBtnText: {
    color: '#FFF',
    fontSize: 15,
    fontWeight: 'bold',
    letterSpacing: 0.3,
  },
  confirmVisualBtn: {
    height: 48,
    borderRadius: 12,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#00C853',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 3,
  },
  confirmVisualBtnText: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: 'bold',
  },
  confirmedVisualBadge: {
    height: 48,
    borderRadius: 12,
    backgroundColor: '#E8F5E9',
    borderWidth: 1,
    borderColor: '#A5D6A7',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  confirmedVisualText: {
    color: '#2E7D32',
    fontSize: 14,
    fontWeight: 'bold',
  },
});
